// Which expected fields a case has not documented, per section, so the page can show
// "Not documented" placeholders in place instead of hiding empty fields.
//
// Source of truth is the validator's completeness result (`completeness.missing`, path shapes
// "case.description", "victim.gender", "violation.accusations"). That list is a snapshot from the
// last run, so every entry is re-checked against the current case data before it is shown —
// a field filled since the run drops out without a re-validation. Cases with no validation
// result (primary data, manual entry) fall back to a fixed baseline subset.

export interface GapRow {
  key: string;
  label: string;
  // Wizard step that holds the field (1-based, see STEP_LABELS in types/caseForm.ts).
  step: number;
}

// Friendly labels for the completeness field paths (leaf keys). Also used by the AI panel's
// "Documentation coverage" slide-over so both read the same way.
export const MISSING_LABELS: Record<string, string> = {
  description: 'Description', caseStartDate: 'Incident start date', caseStartDatePrecision: 'Start-date precision',
  country: 'Country', region: 'Region', city: 'City', indigenousGroup: 'Indigenous group',
  violationTypes: 'Violation types', otherViolationTypes: 'Other violation types', sourceInformation: 'Source',
  communityImpact: 'Community impact', familyImpact: 'Family impact', 'caseEndDate(concluded)': 'End date',
  affectedIndividualsCount: 'Total individuals affected', 'affectedCount>0': 'People affected',
  groupName: 'Group name', indigenousGroupOfVictim: 'Indigenous group', name: 'Name', gender: 'Gender',
  violationType: 'Violation type', perpetratorKnown_answered: 'Perpetrator known', perpetratorTypes: 'Perpetrator types',
  whyVictimsKilled: 'Why victims killed', howKillingCarriedOut: 'How killing carried out',
  accusations: 'Accusations', whoFiledAccusations: 'Who filed accusations',
  affectedCount: 'People count', genderBreakdown: 'Gender breakdown', ageBreakdown: 'Age breakdown',
  impactTypes: 'Types of impact', groupType: 'Type of group',
  criminalizationTypes: 'Form of criminalization', criminalizationTriggers: 'Criminalized activities',
  legalInstrumentTypes: 'Legal instrument categories', proceedingType: 'Type of proceeding',
};

const label = (leaf: string) =>
  MISSING_LABELS[leaf] || leaf.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim();

const filled = (v: unknown): boolean => {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (Array.isArray(v)) return v.length > 0;
  return true;
};

// Baseline shown when there is no validation result to go by.
const FALLBACK_MISSING = [
  'case.description', 'case.caseStartDate', 'case.country', 'case.region', 'case.indigenousGroup',
  'case.violationTypes', 'case.sourceInformation',
  'victim.indigenousGroupOfVictim', 'victim.name', 'victim.gender',
  'victim.groupName', 'victim.groupType', 'victim.affectedIndividualsCount',
];

const CASE_SECTION: Record<string, 'incident' | 'location' | 'indigenousGroup' | 'impact' | 'sources'> = {
  description: 'incident', caseStartDate: 'incident', caseStartDatePrecision: 'incident',
  'caseEndDate(concluded)': 'incident', violationTypes: 'incident', otherViolationTypes: 'incident',
  country: 'location', region: 'location', city: 'location',
  indigenousGroup: 'indigenousGroup',
  communityImpact: 'impact', familyImpact: 'impact', impactTypes: 'impact',
  sourceInformation: 'sources',
};
const CASE_STEP: Record<string, number> = {
  description: 3, caseStartDate: 3, caseStartDatePrecision: 3, 'caseEndDate(concluded)': 3, country: 3, region: 3, city: 3,
  indigenousGroup: 5, violationTypes: 4, otherViolationTypes: 4,
  communityImpact: 6, familyImpact: 6, impactTypes: 6,
  sourceInformation: 1,
};

export interface CaseGaps {
  incident: GapRow[];
  location: GapRow[];
  indigenousGroup: GapRow[];
  impact: GapRow[];
  sources: GapRow[];
  victim: (v: Record<string, any>) => GapRow[];
  violation: (d: Record<string, any>) => GapRow[];
  // True when the rows come from the baseline subset rather than a validation result.
  fromFallback: boolean;
}

// Is a case-level expected field still unfilled right now?
const caseStillMissing = (c: Record<string, any>, key: string): boolean => {
  if (key === 'caseEndDate(concluded)') return c.isCaseOngoing === false && !filled(c.caseEndDate);
  if (key === 'otherViolationTypes') return (c.violationTypes || []).includes('OTHER') && !filled(c.otherViolationTypes);
  return !filled(c[key]);
};

export function computeGaps(caseData: Record<string, any>, missing: string[] | null | undefined): CaseGaps {
  const fromFallback = !missing;
  const paths = missing ?? FALLBACK_MISSING;
  const caseKeys = new Set<string>();
  const victimKeys = new Set<string>();
  const violationKeys = new Set<string>();
  for (const p of paths) {
    if (p.startsWith('case.')) caseKeys.add(p.slice(5));
    else if (p.startsWith('victim.violation.')) continue; // per-violation demographics: the breakdown table already shows dashes
    else if (p.startsWith('victim.')) victimKeys.add(p.slice(7));
    else if (p.startsWith('violation.')) violationKeys.add(p.slice(10));
  }

  const out: CaseGaps = {
    incident: [], location: [], indigenousGroup: [], impact: [], sources: [],
    fromFallback,
    victim: (v) => {
      const rows: GapRow[] = [];
      const add = (key: string, isMissing: boolean) => { if (victimKeys.has(key) && isMissing) rows.push({ key, label: label(key), step: 5 }); };
      if (v.isGroup) {
        add('groupName', !filled(v.groupName));
        add('groupType', !filled(v.groupType));
        add('affectedIndividualsCount', !((v.affectedIndividualsCount || 0) > 0));
      } else {
        // A non-Indigenous victim (advocate / supporter) has no Indigenous group to document.
        if (v.isIndigenous !== false) add('indigenousGroupOfVictim', !filled(v.indigenousGroupOfVictim));
        add('name', !(filled(v.firstName) || filled(v.lastName)));
        add('gender', !(filled(v.gender) && v.gender !== 'UNKNOWN'));
      }
      return rows;
    },
    violation: (d) => {
      const rows: GapRow[] = [];
      const add = (key: string, isMissing: boolean) => { if (violationKeys.has(key) && isMissing) rows.push({ key, label: label(key), step: 4 }); };
      add('perpetratorKnown_answered', d.perpetratorKnown === null || d.perpetratorKnown === undefined);
      if (d.perpetratorKnown === true) add('perpetratorTypes', !filled(d.perpetratorTypes));
      if (d.violationType === 'KILLING') {
        add('whyVictimsKilled', !filled(d.whyVictimsKilled));
        add('howKillingCarriedOut', !filled(d.howKillingCarriedOut));
      } else if (d.violationType === 'CRIMINALIZATION') {
        for (const k of ['accusations', 'whoFiledAccusations', 'criminalizationTypes', 'criminalizationTriggers', 'legalInstrumentTypes', 'proceedingType']) {
          add(k, !filled(d[k]));
        }
      }
      return rows;
    },
  };

  for (const key of Array.from(caseKeys)) {
    const section = CASE_SECTION[key];
    if (!section) continue;
    if (!caseStillMissing(caseData, key)) continue;
    out[section].push({ key, label: label(key), step: CASE_STEP[key] ?? 3 });
  }
  return out;
}
